import { useEffect, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { Modale } from '../ui/Modale';
import { BoutonPrincipal, BoutonSecondaire, MessageErreur, Pastille } from '../ui/Primitives';
import { useToast } from '../ui/useToast';
import { ErreurApi } from '../../lib/api';
import {
  useAnalyserDetection,
  useSupprimerDetection,
  type ImpactDetection,
  type LigneDetection,
} from '../../lib/useVeille';
import { CLASSE_LIBELLE, CLASSE_ZONE_TEXTE, instantLisible, jourLisible } from './format';

/**
 * « Analyser l'impact » d'une évolution relevée (CDC §6.11) : l'équipe dit si
 * elle touche TOP FORMATION et ce qu'il faut faire. L'analyse referme l'alerte
 * de l'évolution ; elle se corrige. Une évolution relevée par le robot ne se
 * supprime pas, une évolution saisie oui.
 */

const IMPACTS: { valeur: ImpactDetection; libelle: string; aide: string }[] = [
  { valeur: 'IMPACT', libelle: 'Impact — action à mener', aide: 'Décrire ce qu’il faut faire, et par qui.' },
  { valeur: 'SANS_IMPACT', libelle: 'Sans impact', aide: 'L’évolution ne touche pas l’organisme.' },
];

export function ModaleDetection({
  detection,
  onFermer,
}: {
  detection: LigneDetection | null;
  onFermer: () => void;
}): JSX.Element | null {
  const notifier = useToast();
  const analyser = useAnalyserDetection();
  const supprimer = useSupprimerDetection();
  const [impact, setImpact] = useState<ImpactDetection | null>(null);
  const [analyse, setAnalyse] = useState('');
  const [confirmerRetrait, setConfirmerRetrait] = useState(false);

  useEffect(() => {
    if (!detection) return;
    setImpact(detection.impact);
    setAnalyse(detection.analyse ?? '');
    setConfirmerRetrait(false);
    analyser.reset();
    supprimer.reset();
    // Réinitialisée à chaque évolution ouverte.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detection?.id]);

  if (!detection) return null;

  function valider(): void {
    if (!detection || !impact) return;
    analyser.mutate(
      { id: detection.id, impact, analyse: analyse.trim() || null },
      {
        onSuccess: () => {
          notifier('Analyse d’impact enregistrée');
          onFermer();
        },
      },
    );
  }

  function retirer(): void {
    if (!detection) return;
    supprimer.mutate(detection.id, {
      onSuccess: () => {
        notifier('Évolution retirée');
        onFermer();
      },
    });
  }

  const erreur = analyser.error ?? supprimer.error;

  return (
    <Modale
      titre={detection.titre}
      sousTitre={`${detection.sourceLibelle} · relevée le ${instantLisible(detection.detecteeLe)}`}
      ouverte
      onFermer={onFermer}
    >
      <div className="space-y-4 p-4 sm:p-6">
        <div className="flex flex-wrap items-center gap-2">
          <Pastille texte={detection.niveauLibelle} ton={detection.ton} />
          {detection.survenueLe && (
            <span className="text-xs text-codialis-gray">À la source le {jourLisible(detection.survenueLe)}</span>
          )}
          {detection.saisiePar && (
            <span className="text-xs text-codialis-gray">Saisie par {detection.saisiePar}</span>
          )}
        </div>

        <p className="text-sm text-codialis-navy">{detection.detail}</p>

        {(detection.avant || detection.apres) && (
          <dl className="grid grid-cols-1 gap-2 rounded-xl bg-codialis-sand p-3 text-sm sm:grid-cols-2">
            <div className="min-w-0">
              <dt className="text-[11px] font-bold uppercase tracking-wide text-codialis-gray">Avant</dt>
              <dd className="break-words text-codialis-navy">{detection.avant ?? '—'}</dd>
            </div>
            <div className="min-w-0">
              <dt className="text-[11px] font-bold uppercase tracking-wide text-codialis-gray">Après</dt>
              <dd className="break-words text-codialis-navy">{detection.apres ?? '—'}</dd>
            </div>
          </dl>
        )}

        {detection.lien && (
          <a
            href={detection.lien}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex min-h-[44px] items-center gap-1.5 text-sm font-semibold text-codialis-sky hover:underline"
          >
            Voir à la source
            <ExternalLink className="h-4 w-4" aria-hidden="true" />
          </a>
        )}

        <fieldset>
          <legend className={CLASSE_LIBELLE}>Analyse d’impact</legend>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {IMPACTS.map((choix) => (
              <label
                key={choix.valeur}
                className={`flex min-h-[44px] cursor-pointer items-start gap-3 rounded-xl border px-3 py-2.5 transition ${
                  impact === choix.valeur ? 'border-codialis-navy bg-codialis-navy/5' : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  name="impact"
                  checked={impact === choix.valeur}
                  onChange={() => setImpact(choix.valeur)}
                  className="mt-0.5 h-5 w-5 shrink-0"
                />
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-codialis-navy">{choix.libelle}</span>
                  <span className="block text-[11px] text-codialis-gray">{choix.aide}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        <label className="block">
          <span className={CLASSE_LIBELLE}>
            {impact === 'IMPACT' ? 'Ce qu’il faut faire' : 'Commentaire (facultatif)'}
          </span>
          <textarea
            value={analyse}
            onChange={(evenement) => setAnalyse(evenement.target.value)}
            rows={4}
            className={CLASSE_ZONE_TEXTE}
          />
        </label>

        {detection.analyseePar && detection.analyseeLe && (
          <p className="text-[11px] text-codialis-gray">
            Analysée par {detection.analyseePar} le {instantLisible(detection.analyseeLe)}
          </p>
        )}

        {erreur instanceof ErreurApi && <MessageErreur message={erreur.message} />}

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            {detection.refusSuppression === null &&
              (confirmerRetrait ? (
                <button
                  type="button"
                  onClick={retirer}
                  disabled={supprimer.isPending}
                  className="inline-flex min-h-[44px] w-full items-center justify-center rounded-xl bg-red-600 px-4 text-sm font-semibold text-white transition hover:bg-red-700 disabled:opacity-60 sm:w-auto"
                >
                  {supprimer.isPending ? 'Retrait…' : 'Confirmer le retrait'}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmerRetrait(true)}
                  className="inline-flex min-h-[44px] w-full items-center justify-center rounded-xl border border-red-200 px-4 text-sm font-semibold text-red-700 transition hover:bg-red-50 sm:w-auto"
                >
                  Retirer l’évolution
                </button>
              ))}
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <BoutonSecondaire onClick={onFermer}>Annuler</BoutonSecondaire>
            <BoutonPrincipal onClick={valider} disabled={!impact || analyser.isPending}>
              {analyser.isPending
                ? 'Enregistrement…'
                : detection.statut === 'ANALYSEE'
                  ? 'Mettre à jour l’analyse'
                  : 'Enregistrer l’analyse'}
            </BoutonPrincipal>
          </div>
        </div>
      </div>
    </Modale>
  );
}
